/**
 * "Customer type": whether a customer is real or synthetic (a test, E2E or demo tenant). Super Admin only.
 *
 * A synthetic customer is excluded from business reporting (billing runs, cross-tenant job lists, the
 * automatic invitation sender, customer alert emails). It is NEVER excluded from safety limits: its real
 * unlocks still count against the shared monthly ceiling. Say both, so nobody flags a customer expecting
 * it to be free to run.
 *
 * Flagging a REAL customer by mistake would hide its billing and alerts, so changing the flag needs a
 * reason, and the backend refuses (409) a customer whose signup was paid through a live Stripe checkout
 * until it is confirmed a second time here. Every change also emails Avrio (avrio-backend#213).
 *
 * PUT /api/customers/{id}/synthetic { isSynthetic, reason, confirmRealCustomer? }.
 */
import { memo, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Typography,
} from 'antd';
import axios from '../../../../axiosConfig';
import { CustomerConfig } from '../../types';
import styles from './index.module.css';

const { Title, Text } = Typography;

interface Props {
  customerId: number;
  customerName: string;
  isSynthetic: boolean;
  reason?: string;
  /** Called with the customer as the backend now has it, so the page can show the new state. */
  onChanged: (updated: CustomerConfig) => void;
}

const LIVE_STRIPE_MESSAGE =
  'This customer’s signup was paid through a live Stripe checkout, so it looks like a real customer. Flagging it synthetic would hide its billing and alerts.';

const SyntheticCustomerSection = ({
  customerId,
  customerName,
  isSynthetic,
  reason,
  onChanged,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [form] = Form.useForm<{ reason: string }>();

  const target = !isSynthetic; // what a change would make it

  const close = () => {
    setOpen(false);
    setError(null);
    setNeedsConfirm(false);
    form.resetFields();
  };

  const submit = (confirmRealCustomer: boolean) => {
    const values = form.getFieldsValue();
    setSaving(true);
    setError(null);
    axios
      .put<CustomerConfig>(`/api/customers/${customerId}/synthetic`, {
        isSynthetic: target,
        reason: (values.reason ?? '').trim(),
        ...(confirmRealCustomer ? { confirmRealCustomer: true } : {}),
      })
      .then((res) => {
        onChanged(res.data);
        close();
      })
      .catch((err) => {
        if (err?.response?.status === 409) {
          setNeedsConfirm(true);
          return;
        }
        setError(
          err?.response?.status === 400
            ? (err?.response?.data?.message ?? 'The reason is required.')
            : 'Could not change the customer type. Try again.',
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <section className={styles.section} aria-labelledby="customer-type-title">
      <Title level={5} id="customer-type-title" className={styles.title}>
        Customer type
      </Title>

      {isSynthetic ? (
        <Space direction="vertical" size={4} className={styles.body}>
          <span>
            <Tag color="gold">Synthetic</Tag>
            <Text>{customerName} is not a real customer.</Text>
          </span>
          {reason && <Text type="secondary">Reason: {reason}</Text>}
        </Space>
      ) : (
        <Text className={styles.body}>
          {customerName} is counted as a real customer.
        </Text>
      )}
      <Text type="secondary" className={styles.note}>
        Synthetic customers are left out of business reporting: billing runs,
        cross-tenant job lists, the automatic invitation sender and customer
        alert emails. They still count against every safety limit, including the
        shared monthly unlock ceiling.
      </Text>

      <Button onClick={() => setOpen(true)} danger={!isSynthetic}>
        {isSynthetic ? 'Mark as a real customer…' : 'Mark as synthetic…'}
      </Button>

      <Modal
        open={open}
        title={
          target
            ? `Mark ${customerName} as synthetic?`
            : `Mark ${customerName} as a real customer?`
        }
        okText={target ? 'Mark as synthetic' : 'Mark as real'}
        confirmLoading={saving}
        onCancel={close}
        onOk={() =>
          form
            .validateFields()
            .then(() => submit(needsConfirm))
            // A failed validation is already shown under the field; it must not escape as an unhandled rejection.
            .catch(() => undefined)
        }
        okButtonProps={{ danger: target && needsConfirm }}
        maskClosable={!saving}
      >
        <Text>
          {target
            ? 'It will be left out of business reporting. Avrio is emailed about this change.'
            : 'It will be counted as a real customer again. Avrio is emailed about this change.'}
        </Text>
        <Form form={form} layout="vertical" className={styles.form}>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'A reason is required',
              },
              { max: 255, message: 'At most 255 characters' },
            ]}
          >
            <Input.TextArea
              rows={2}
              maxLength={255}
              placeholder={
                target
                  ? 'e.g. Internal E2E test tenant'
                  : 'e.g. Signed a real contract'
              }
            />
          </Form.Item>
        </Form>
        {needsConfirm && (
          <Alert
            type="warning"
            showIcon
            message="This looks like a real customer"
            description={`${LIVE_STRIPE_MESSAGE} Press the button again to flag it anyway.`}
            className={styles.notice}
          />
        )}
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            className={styles.notice}
          />
        )}
      </Modal>
    </section>
  );
};

export default memo(SyntheticCustomerSection);
